import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, formatEUR } from "@/components/app/Shared";
import LiveFeed from "@/components/app/LiveFeed";
import { Building2, Users, ShieldCheck, Plus, Trash2, LogOut, Crown, Layers, Sparkles, Activity, TrendingUp, Server, Wifi, Clock, Database, KeyRound, Copy, RefreshCw, UserPlus, Mail, Eye, EyeOff, Check, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const PLAN_BADGE = {
  basic:      { label: "Starter", cls: "bg-zinc-100 text-zinc-700 border-zinc-200",  icon: Layers },
  pro:        { label: "Growth",  cls: "bg-blue-50 text-blue-700 border-blue-200",   icon: Sparkles },
  enterprise: { label: "Pro",     cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Crown },
};

export default function PlatformDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("health"); // health | revenue | feed | orgs | users | logs
  const [logs, setLogs] = useState([]);
  const [health, setHealth] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: "", plan: "basic" });

  const load = async () => {
    try {
      const [o, u, l, h, r] = await Promise.all([
        api.get("/platform/organizations"),
        api.get("/platform/users"),
        api.get("/platform/audit-logs"),
        api.get("/platform/health"),
        api.get("/platform/revenue"),
      ]);
      setOrgs(o.data); setUsers(u.data); setLogs(l.data); setHealth(h.data); setRevenue(r.data);
    } catch { toast.error("Laden mislukt"); }
  };
  useEffect(() => {
    load();
    const t = setInterval(() => {
      api.get("/platform/health").then((r) => setHealth(r.data)).catch(() => {});
    }, 10000);
    return () => clearInterval(t);
  }, []);

  const changePlan = async (oid, plan) => {
    try {
      await api.patch(`/platform/organizations/${oid}`, { plan });
      toast.success("Plan bijgewerkt");
      load();
    } catch { toast.error("Wijzigen mislukt"); }
  };

  const removeOrg = async (oid, name) => {
    if (!confirm(`Verwijder organisatie "${name}" en ALLE data? Dit kan niet ongedaan worden gemaakt.`)) return;
    try {
      await api.delete(`/platform/organizations/${oid}`);
      toast.success("Verwijderd");
      load();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  const createOrg = async (e) => {
    e.preventDefault();
    try {
      await api.post("/platform/organizations", newOrg);
      toast.success("Aangemaakt");
      setShowCreate(false); setNewOrg({ name: "", plan: "basic" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="platform-dashboard">
      <header className="sticky top-0 z-20 bg-white border-b border-zinc-200 px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck size={20} className="text-[#5BC044]" />
          <div className="font-bold tracking-tight">Stockly · Platform</div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-zinc-500 hidden sm:inline">{user?.name} · {user?.email}</span>
          <button onClick={() => nav("/platform/auto-fill")}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-[#5BC044] px-2"
            data-testid="nav-auto-fill">
            <Sparkles size={15} /> Aanvul-instellingen
          </button>
          <button onClick={() => nav("/platform/modules")}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-[#5BC044] px-2"
            data-testid="nav-modules">
            <Layers size={15} /> Modules
          </button>
          <button onClick={() => nav("/platform/haccp")}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-[#5BC044] px-2"
            data-testid="nav-platform-haccp">
            <GraduationCap size={15} /> HACCP
          </button>
          <button onClick={async () => { await logout(); nav("/login"); }} className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900" data-testid="logout-button">
            <LogOut size={15} /> Uitloggen
          </button>
        </div>
      </header>

      <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
        <PageHeader
          overline="Super-admin"
          title="Platform overzicht"
          description="Beheer alle organisaties, plannen en gebruikers."
        />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Kpi icon={Building2}  label="Organisaties"        value={orgs.length} accent />
          <Kpi icon={TrendingUp} label="MRR"                 value={revenue ? `€${revenue.mrr}` : "—"} />
          <Kpi icon={Activity}   label="Active sessions"     value={health?.active_sessions ?? "—"} />
          <Kpi icon={Wifi}       label="API health"          value={health ? `${health.api_health_pct}%` : "—"} />
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <TabBtn active={tab === "health"}   onClick={() => setTab("health")}   testid="tab-health">Platform health</TabBtn>
          <TabBtn active={tab === "revenue"}  onClick={() => setTab("revenue")}  testid="tab-revenue">Revenue</TabBtn>
          <TabBtn active={tab === "feed"}     onClick={() => setTab("feed")}     testid="tab-feed">Live feed</TabBtn>
          <TabBtn active={tab === "orgs"}     onClick={() => setTab("orgs")}     testid="tab-orgs">Organisaties</TabBtn>
          <TabBtn active={tab === "onboard"}  onClick={() => setTab("onboard")}  testid="tab-onboard">Onboarding</TabBtn>
          <TabBtn active={tab === "codes"}    onClick={() => setTab("codes")}    testid="tab-codes">Deelcodes</TabBtn>
          <TabBtn active={tab === "users"}    onClick={() => setTab("users")}    testid="tab-users">Gebruikers</TabBtn>
          <TabBtn active={tab === "logs"}     onClick={() => setTab("logs")}     testid="tab-logs">Audit logs</TabBtn>
        </div>

        {tab === "health" && health && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <HealthCard icon={Server}   label="Uptime"          value={health.uptime_human}   sub={`${health.uptime_pct}%`} ok />
            <HealthCard icon={Clock}    label="Avg latency"     value={`${health.avg_response_ms} ms`} sub={`p95: ${health.p95_response_ms} ms`} ok={health.avg_response_ms < 200} />
            <HealthCard icon={Wifi}     label="API health"      value={`${health.api_health_pct}%`} sub={`${health.failed_5m} failed / 5m`} ok={health.api_health_pct >= 99} />
            <HealthCard icon={Database} label="Database"        value={health.db_ok ? "Online" : "Offline"} sub={`ping: ${health.db_ping_ms} ms`} ok={health.db_ok} />
            <HealthCard icon={Activity} label="Active sessions" value={health.active_sessions} sub={`${health.total_users} total users`} ok />
            <HealthCard icon={Layers}   label="Requests (5m)"   value={health.requests_5m}    sub={`${health.requests_total} totaal sinds boot`} ok />
          </div>
        )}

        {tab === "revenue" && revenue && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi icon={TrendingUp} label="MRR"           value={`€${revenue.mrr}`} accent />
              <Kpi icon={Sparkles}   label="ARR"           value={`€${revenue.arr}`} />
              <Kpi icon={Crown}      label="Paid transactions" value={revenue.total_paid_count} />
              <Kpi icon={Layers}     label="Total revenue" value={formatEUR(revenue.revenue_total)} />
            </div>
            <div className="card-flat p-5">
              <h3 className="font-bold text-[#1B2D5C] tracking-tight mb-4">Revenue per maand (laatste 6)</h3>
              <RevenueChart data={revenue.by_month} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="card-flat p-5">
                <h3 className="font-bold text-[#1B2D5C] tracking-tight mb-3">Top paying orgs</h3>
                {revenue.top_orgs.length === 0 ? (
                  <div className="text-sm text-zinc-500 py-6 text-center">Nog geen betaalde upgrades.</div>
                ) : revenue.top_orgs.map((o, i) => (
                  <div key={o.organization_id} className="flex justify-between py-2 border-b border-zinc-50 last:border-0">
                    <span className="font-medium">{i + 1}. {o.name}</span>
                    <span className="font-mono font-bold text-[#5BC044]">€{o.revenue}</span>
                  </div>
                ))}
              </div>
              <div className="card-flat p-5">
                <h3 className="font-bold text-[#1B2D5C] tracking-tight mb-3">Plan distributie</h3>
                {Object.entries(revenue.counts_by_plan).map(([plan, count]) => {
                  const total = Object.values(revenue.counts_by_plan).reduce((a, b) => a + b, 0);
                  const pct = total ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={plan} className="mb-3">
                      <div className="flex justify-between text-sm mb-1"><span className="capitalize font-semibold">{plan}</span><span className="font-mono">{count} ({pct}%)</span></div>
                      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <div className={`h-full ${plan === "enterprise" ? "bg-amber-500" : plan === "pro" ? "bg-blue-500" : "bg-zinc-400"}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {tab === "feed" && <LiveFeed />}

        {tab === "orgs" && (
          <>
            <div className="flex justify-end mb-3">
              <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 h-10 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-md text-sm font-semibold" data-testid="create-org-button">
                <Plus size={16} /> Nieuwe organisatie
              </button>
            </div>
            <div className="card-flat overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left">
                    <th className="px-4 py-3 overline">Organisatie</th>
                    <th className="px-4 py-3 overline">Plan</th>
                    <th className="px-4 py-3 overline text-right">Restaurants</th>
                    <th className="px-4 py-3 overline text-right">Gebruikers</th>
                    <th className="px-4 py-3 overline text-right">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {orgs.map((o) => {
                    const meta = PLAN_BADGE[o.plan] || PLAN_BADGE.basic;
                    const Icon = meta.icon;
                    return (
                      <tr key={o.id} className="hover:bg-zinc-50" data-testid={`org-row-${o.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-[#1B2D5C]">{o.name}</div>
                          <div className="text-xs text-zinc-500 font-mono">{o.owner_email || "—"}</div>
                        </td>
                        <td className="px-4 py-3">
                          <select value={o.plan} onChange={(e) => changePlan(o.id, e.target.value)} className={`text-xs font-semibold px-2 py-1 border rounded ${meta.cls}`} data-testid={`org-plan-${o.id}`}>
                            <option value="basic">Starter (1)</option>
                            <option value="pro">Growth (3)</option>
                            <option value="enterprise">Pro (∞)</option>
                          </select>
                          <div className="text-[10px] text-zinc-400 mt-1 inline-flex items-center gap-1"><Icon size={11} /> {meta.label}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {o.usage?.restaurants ?? 0} / {o.plan_limit === -1 ? "∞" : o.plan_limit}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">{o.usage?.users ?? 0}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => removeOrg(o.id, o.name)} className="text-zinc-400 hover:text-red-600 p-1" title="Verwijderen" data-testid={`org-delete-${o.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "users" && (
          <div className="card-flat overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left">
                  <th className="px-4 py-3 overline">Naam</th>
                  <th className="px-4 py-3 overline">E-mail</th>
                  <th className="px-4 py-3 overline">Rol</th>
                  <th className="px-4 py-3 overline">Organisatie</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-zinc-50" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium">{u.name}</td>
                    <td className="px-4 py-3 text-zinc-600 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                    <td className="px-4 py-3 text-zinc-700">{u.organization_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "logs" && (
          <div className="card-flat overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left">
                  <th className="px-4 py-3 overline">Wanneer</th>
                  <th className="px-4 py-3 overline">Actor</th>
                  <th className="px-4 py-3 overline">Actie</th>
                  <th className="px-4 py-3 overline">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {logs.length === 0 ? (
                  <tr><td colSpan="4" className="px-4 py-8 text-center text-sm text-zinc-500">Nog geen audit events.</td></tr>
                ) : logs.map((l) => (
                  <tr key={l.id} data-testid={`log-row-${l.id}`}>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-500">{new Date(l.created_at).toLocaleString("nl-NL")}</td>
                    <td className="px-4 py-3"><div className="text-sm font-medium">{l.actor_name}</div><div className="text-xs text-zinc-500">{l.actor_email}</div></td>
                    <td className="px-4 py-3"><span className="px-2 py-1 bg-zinc-100 rounded text-xs font-mono">{l.action}</span></td>
                    <td className="px-4 py-3 text-xs text-zinc-600 font-mono">{l.details ? JSON.stringify(l.details).slice(0, 120) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "onboard" && <OnboardingTab onCreated={load} />}

        {tab === "codes" && <DeelcodesTab />}

        {showCreate && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)} data-testid="create-org-modal">
            <form onClick={(e) => e.stopPropagation()} onSubmit={createOrg} className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl space-y-3">
              <h3 className="text-xl font-bold tracking-tight text-[#1B2D5C] mb-2">Nieuwe organisatie</h3>
              <div>
                <label className="overline block mb-1.5">Naam</label>
                <input required value={newOrg.name} onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })} className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" data-testid="new-org-name" />
              </div>
              <div>
                <label className="overline block mb-1.5">Plan</label>
                <select value={newOrg.plan} onChange={(e) => setNewOrg({ ...newOrg, plan: e.target.value })} className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white" data-testid="new-org-plan">
                  <option value="basic">Starter — 1 locatie (€19/mnd)</option>
                  <option value="pro">Growth — 3 locaties (€49/mnd)</option>
                  <option value="enterprise">Pro — onbeperkte locaties (€99/mnd)</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 h-10 border border-zinc-300 rounded-md text-sm">Annuleren</button>
                <button type="submit" className="flex-1 h-10 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-md text-sm font-semibold" data-testid="submit-new-org">Aanmaken</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="card-flat p-5">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center mb-3 ${accent ? "bg-[#5BC044]/10 text-[#5BC044]" : "bg-zinc-100 text-zinc-700"}`}>
        <Icon size={18} strokeWidth={1.8} />
      </div>
      <div className="overline">{label}</div>
      <div className="text-3xl font-bold tracking-tight mt-1 font-mono">{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} className={`h-10 px-4 rounded-md text-sm font-semibold transition-colors ${active ? "bg-[#1B2D5C] text-white" : "bg-white border border-zinc-200 text-zinc-700 hover:border-[#5BC044]"}`} data-testid={testid}>{children}</button>
  );
}

function RoleBadge({ role }) {
  const map = {
    super_admin: "bg-purple-100 text-purple-700",
    support: "bg-blue-100 text-blue-700",
    cybersecurity: "bg-purple-100 text-purple-700",
    organization_admin: "bg-emerald-100 text-emerald-700",
    admin: "bg-emerald-100 text-emerald-700",
    chef: "bg-amber-100 text-amber-700",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${map[role] || "bg-zinc-100 text-zinc-700"}`}>{role}</span>;
}

function HealthCard({ icon: Icon, label, value, sub, ok }) {
  return (
    <div className="card-flat p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${ok ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
          <Icon size={18} strokeWidth={1.8} />
        </div>
        <span className={`w-2 h-2 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"} ${ok ? "animate-pulse" : ""}`} />
      </div>
      <div className="overline">{label}</div>
      <div className="text-2xl font-bold tracking-tight font-mono mt-1">{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{sub}</div>
    </div>
  );
}

function RevenueChart({ data }) {
  const max = Math.max(...data.map((d) => d.amount), 1);
  return (
    <div className="flex items-end gap-3 h-40">
      {data.map((m) => (
        <div key={m.key} className="flex-1 flex flex-col items-center justify-end h-full">
          <div className="text-xs font-mono font-semibold text-[#1B2D5C] mb-1">€{m.amount}</div>
          <div
            className="w-full bg-gradient-to-t from-[#5BC044] to-[#5BC044]/40 rounded-t min-h-[4px]"
            style={{ height: `${(m.amount / max) * 100}%` }}
          />
          <div className="text-[10px] font-mono text-zinc-500 mt-1.5">{m.label}</div>
        </div>
      ))}
    </div>
  );
}


function OnboardingTab({ onCreated }) {
  const [form, setForm] = useState({
    org_name: "", plan: "basic",
    admin_name: "", admin_email: "", admin_password: "",
    restaurant_name: "", restaurant_location: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const genPw = () => {
    const set = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!#$";
    let s = "";
    for (let i = 0; i < 12; i++) s += set[Math.floor(Math.random() * set.length)];
    setForm((f) => ({ ...f, admin_password: s }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/platform/onboard", form);
      setResult(data);
      toast.success(`${data.organization.name} aangemaakt — ${data.products_copied} producten gekopieerd`);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Onboarding mislukt");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setForm({ org_name: "", plan: "basic", admin_name: "", admin_email: "", admin_password: "", restaurant_name: "", restaurant_location: "" });
  };

  if (result) {
    return (
      <div className="card-flat p-6 max-w-2xl" data-testid="onboarding-success">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-[#5BC044]"><Check size={20} /></div>
          <div>
            <h3 className="text-xl font-bold tracking-tight text-[#1B2D5C]">Onboarding compleet</h3>
            <p className="text-sm text-zinc-500">Nieuwe organisatie aangemaakt met admin-account en master producten.</p>
          </div>
        </div>
        <div className="space-y-3 text-sm">
          <Row label="Organisatie">{result.organization.name} <Pill>{result.organization.plan}</Pill></Row>
          <Row label="Restaurant">{result.restaurant.name} — {result.restaurant.location}</Row>
          <Row label="Admin">{result.admin.name} ({result.admin.email})</Row>
          <Row label="Producten gekopieerd"><b className="text-[#5BC044]">{result.products_copied}</b></Row>
          <Row label="Deelcode">
            <code className="bg-amber-50 text-amber-900 border border-amber-200 px-2 py-1 rounded font-mono text-sm">{result.restaurant.invite_code}</code>
            <CopyBtn text={result.restaurant.invite_code} />
          </Row>
        </div>
        <div className="flex gap-2 pt-5 mt-4 border-t border-zinc-100">
          <button onClick={reset} className="flex-1 h-10 bg-[#1B2D5C] hover:bg-[#142244] text-white rounded-md text-sm font-semibold" data-testid="onboard-another">Nog een aanmaken</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-flat p-6 max-w-2xl" data-testid="onboarding-form">
      <div className="flex items-center gap-2 mb-5">
        <UserPlus size={20} className="text-[#5BC044]" />
        <h3 className="text-xl font-bold tracking-tight text-[#1B2D5C]">Nieuwe klant onboarden</h3>
      </div>
      <p className="text-sm text-zinc-500 mb-5">Maak in één keer een organisatie + admin + restaurant aan. Master producten worden automatisch gekopieerd.</p>

      <div className="space-y-3">
        <Section title="Organisatie">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Naam organisatie *">
              <input required value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} className="form-input" placeholder="Bistro De Vink" data-testid="onboard-org-name" />
            </Field>
            <Field label="Plan">
              <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="form-input" data-testid="onboard-plan">
                <option value="basic">Starter — 1 locatie (€19)</option>
                <option value="pro">Growth — 3 locaties (€49)</option>
                <option value="enterprise">Pro — onbeperkt (€99)</option>
              </select>
            </Field>
          </div>
        </Section>
        <Section title="Admin gebruiker">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Volledige naam *">
              <input required value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="form-input" placeholder="Jan de Vries" data-testid="onboard-admin-name" />
            </Field>
            <Field label="E-mail *">
              <input required type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="form-input" placeholder="jan@devink.nl" data-testid="onboard-admin-email" />
            </Field>
          </div>
          <Field label="Wachtwoord *">
            <div className="relative">
              <input required minLength={6} type={showPw ? "text" : "password"} value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="form-input pr-20" placeholder="Minimaal 6 tekens" data-testid="onboard-admin-pw" />
              <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <button type="button" onClick={() => setShowPw((v) => !v)} className="w-8 h-8 inline-flex items-center justify-center text-zinc-400 hover:text-[#1B2D5C]" title={showPw ? "Verbergen" : "Tonen"} data-testid="onboard-pw-toggle">
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button type="button" onClick={genPw} className="w-8 h-8 inline-flex items-center justify-center text-zinc-400 hover:text-[#5BC044]" title="Genereer sterk wachtwoord" data-testid="onboard-pw-gen">
                  <RefreshCw size={14} />
                </button>
              </div>
            </div>
          </Field>
        </Section>
        <Section title="Restaurant (locatie)">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Naam restaurant">
              <input value={form.restaurant_name} onChange={(e) => setForm({ ...form, restaurant_name: e.target.value })} className="form-input" placeholder="(leeg = zelfde als org)" data-testid="onboard-resto-name" />
            </Field>
            <Field label="Locatie/stad">
              <input value={form.restaurant_location} onChange={(e) => setForm({ ...form, restaurant_location: e.target.value })} className="form-input" placeholder="Amsterdam" data-testid="onboard-resto-loc" />
            </Field>
          </div>
        </Section>
      </div>

      <button type="submit" disabled={loading} className="mt-6 w-full h-11 bg-[#5BC044] hover:bg-[#4FAA3A] disabled:opacity-60 text-white rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="onboard-submit">
        {loading ? "Bezig…" : <><UserPlus size={16} /> Onboarden + producten kopiëren</>}
      </button>
    </form>
  );
}

function DeelcodesTab() {
  const [restos, setRestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/platform/restaurants");
      setRestos(data);
    } catch { toast.error("Laden mislukt"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const regen = async (rid, name) => {
    if (!window.confirm(`Nieuwe deelcode voor ${name}? De oude code werkt daarna niet meer.`)) return;
    try {
      const { data } = await api.post(`/platform/restaurants/${rid}/regenerate-invite`);
      toast.success(`Nieuwe code: ${data.invite_code}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mislukt");
    }
  };

  const filtered = restos.filter((r) => !search || r.name.toLowerCase().includes(search.toLowerCase()) || (r.organization_name || "").toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="text-zinc-500 text-sm py-6">Laden…</div>;
  return (
    <div data-testid="deelcodes-tab">
      <div className="mb-4 flex items-center gap-2">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Zoek restaurant of organisatie…" className="h-10 px-3 border border-zinc-300 rounded-md text-sm w-full max-w-sm" data-testid="deelcodes-search" />
        <span className="text-xs text-zinc-500 font-mono">{filtered.length} / {restos.length}</span>
      </div>
      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <th className="px-4 py-3 overline">Restaurant</th>
              <th className="px-4 py-3 overline">Organisatie</th>
              <th className="px-4 py-3 overline">Deelcode</th>
              <th className="px-4 py-3 overline text-right">Acties</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {filtered.map((r) => (
              <tr key={r.id} className="hover:bg-zinc-50" data-testid={`code-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#1B2D5C]">{r.name}</div>
                  <div className="text-xs text-zinc-500">{r.location}</div>
                </td>
                <td className="px-4 py-3 text-zinc-700">{r.organization_name}</td>
                <td className="px-4 py-3">
                  <code className="bg-amber-50 text-amber-900 border border-amber-200 px-2 py-1 rounded font-mono text-sm tracking-wider" data-testid={`code-value-${r.id}`}>{r.invite_code || "—"}</code>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <CopyBtn text={r.invite_code || ""} testid={`code-copy-${r.id}`} />
                    <button onClick={() => regen(r.id, r.name)} className="inline-flex items-center gap-1 h-8 px-2 border border-zinc-300 hover:border-[#5BC044] rounded text-xs text-zinc-600" data-testid={`code-regen-${r.id}`} title="Nieuwe code">
                      <RefreshCw size={12} /> Vernieuw
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-zinc-400 text-sm">Geen restaurants gevonden.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-zinc-100 rounded-md p-4 bg-zinc-50/30">
      <div className="overline mb-2.5 inline-flex items-center gap-1.5"><KeyRound size={11} /> {title}</div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
function Row({ label, children }) {
  return (
    <div className="flex items-center gap-3">
      <span className="overline w-44 shrink-0">{label}</span>
      <span className="inline-flex items-center gap-2 flex-1 text-[#1B2D5C]">{children}</span>
    </div>
  );
}
function Pill({ children }) {
  return <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{children}</span>;
}
function CopyBtn({ text, testid }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { toast.error("Kopiëren mislukt"); }
  };
  return (
    <button onClick={copy} disabled={!text} type="button" className="inline-flex items-center gap-1 h-8 px-2 border border-zinc-300 hover:border-[#5BC044] rounded text-xs text-zinc-600 disabled:opacity-40" data-testid={testid} title="Kopieer">
      {done ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
    </button>
  );
}
